import cx from "classnames";
import { useState } from "react";
import { Carousel as ReactCarousel } from "react-responsive-carousel";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";

import Icon from "common/components/Icon";
import Image from "common/components/Image";

import { MediaItem, MediaVideo } from "client/common/types";
import { useVideoLightbox } from "client/common/hooks/stackables";

import styles from "../../styles/Carousel.module.less";

type ContentMediaProps = {
  item: MediaItem;
  onVideoSelect: (video: MediaVideo) => void;
};

const exhaustiveCheck = (item: never) => {
  console.warn(`Attempted to get image for invalid item: ${item}`);
  return null;
};

const ContentMedia = ({ item, onVideoSelect }: ContentMediaProps) => {
  switch (item.kind) {
    case "image":
      return <Image alt={item.alt} src={item.contentUrl} className={styles.image} />;
    case "video":
      return (
        <>
          <Image alt={item.alt} src={item.previewImageUrl} className={styles.image} />
          <button className={styles.play} type="button" onClick={() => onVideoSelect(item)}>
            <Icon icon={faPlay} />
          </button>
        </>
      );
    default:
      return exhaustiveCheck(item);
  }
};

type ThumbnailMediaProps = {
  item: MediaItem;
};

const ThumbnailMedia = ({ item }: ThumbnailMediaProps) => {
  switch (item.kind) {
    case "image":
      return <Image alt={item.alt} src={item.contentUrl} className={styles.image} />;
    case "video":
      return <Image alt={item.alt} src={item.previewImageUrl} className={styles.image} />;
    default:
      return exhaustiveCheck(item);
  }
};

type CarouselProps<T> = {
  items: T[];
  selectedIndex: number;
  onSelectIndex: (i: number) => void;
};

const Carousel = <T extends MediaItem>(props: CarouselProps<T>) => {
  const { open: openVideoLightbox } = useVideoLightbox();
  const { items, selectedIndex, onSelectIndex } = props;

  const [isGrabbing, setIsGrabbing] = useState(false);
  const isSingle = items.length <= 1;

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.carousel}>
          <ReactCarousel
            showArrows={false}
            showStatus={false}
            showIndicators={false}
            showThumbs={false}
            infiniteLoop={true}
            selectedItem={selectedIndex}
            onChange={onSelectIndex}
            className={cx(styles.main, isGrabbing && styles.grabbing, isSingle && styles.single)}
            onSwipeStart={() => setIsGrabbing(true)}
            onSwipeEnd={() => setIsGrabbing(false)}
            emulateTouch={true}
          >
            {items.map((item) => (
              <div key={item.key} className={styles.item}>
                <ContentMedia item={item} onVideoSelect={(video) => openVideoLightbox({ video })} />
              </div>
            ))}
          </ReactCarousel>
        </div>
      </div>
      {items.length > 1 && (
        <div className={styles.thumbnails}>
          {items.map((item, i) => (
            <button
              type="button"
              onClick={() => onSelectIndex(i)}
              key={item.key}
              className={cx(styles.thumbnail, selectedIndex === i && styles.active)}
            >
              <ThumbnailMedia item={item} />
              <svg viewBox="0 0 100 100" className={styles.circle}>
                <circle cx="50" cy="50" r="40" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
